"use client";

import { ModalCloseIcon } from "@/app/components/common/Icons";
import { X } from "lucide-react";
import { useTheme } from "next-themes";
import React, {
  useEffect,
  useRef,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";
type ModalProps = {
  isOpen: boolean;
  width: string;
  height: string;
  children: ReactNode;
  setOpenModal?: Dispatch<SetStateAction<boolean>>;
  outerClassName?: string;
  innerClassName?: string;
  bgColor?: string;
  isInputModal?: boolean;
  showCloseIcon?: boolean;
};

const Modal = ({
  children,
  isOpen,
  width,
  height,
  setOpenModal,
  outerClassName = "",
  innerClassName = "",
  bgColor = "dark:bg-zinc-950 bg-white",
  isInputModal = false,
  showCloseIcon = true,
}: ModalProps) => {
  const { resolvedTheme } = useTheme();

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add(
        isInputModal ? "no-scroll-input-modal" : "no-scroll"
      );
    } else {
      document.body.classList.remove(
        isInputModal ? "no-scroll-input-modal" : "no-scroll"
      );
    }

    return () => {
      document.body.classList.remove(
        isInputModal ? "no-scroll-input-modal" : "no-scroll"
      );
    };
  }, [isOpen]);

  const modalRef = useRef<HTMLDivElement | null>(null);

  // Close modal when clicking outside of it
  const handleClickOutside = (event: MouseEvent) => {
    if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
      setOpenModal && setOpenModal(false); // Close modal if clicked outside
    }
  };

  // Add event listener when modal is open, and cleanup on modal close
  useEffect(() => {
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside); // Listen for clicks
    } else {
      document.removeEventListener("mousedown", handleClickOutside); // Clean up
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside); // Clean up on unmount
    };
  }, [isOpen]);

  return (
    isOpen && (
      <div className="modal-overlay z-40 modal-open">
        <div
          className={`${width} relative shadow rounded-xl md:m-8 m-3 ${outerClassName}`}
          ref={modalRef}
        >
          <div className={`rounded-2xl ${bgColor} ${innerClassName}`}>
            <div
              className={`${height} rounded-2xl sm:p-6 p-4 gallery-modal relative`}
            >
              {setOpenModal && showCloseIcon && (
                <button
                  className="p-0 ms-auto absolute  sm:top-8 top-4 right-5 cursor-pointer z-10"
                  onClick={() => setOpenModal(false)}
                >
                  <X />
                </button>
              )}
              {children}
            </div>
          </div>
        </div>
      </div>
    )
  );
};

export default Modal;
